import { useCallback } from 'react';
import type { KeyboardEvent } from 'react';

type ActiveSetter = (updater: (prev: number | null) => number | null) => void;

/**
 * Shared keyboard navigation for the hover / tap charts.
 *
 * The equity, drawdown and bar charts reveal their numbers through a hover or
 * tap cursor. Keyboard users get the same experience here: the arrow keys move
 * a virtual cursor across the data points, while Home / End jump straight to
 * the first or last point. The chart container receives focus and calls the
 * returned handler from its `onKeyDown`.
 */
export function useChartKeyboard(count: number, setActive: ActiveSetter) {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (count === 0) return;
      const { key } = event;
      if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') {
        return;
      }
      event.preventDefault();
      setActive((prev) => {
        const current = prev ?? count - 1;
        if (key === 'ArrowRight') return Math.min(count - 1, current + 1);
        if (key === 'ArrowLeft') return Math.max(0, current - 1);
        if (key === 'Home') return 0;
        return count - 1;
      });
    },
    [count, setActive],
  );
}

export default useChartKeyboard;